import React from "react";
import { AlertCircle, CheckCircle2, Clock3, FileWarning, Info, MapPin, ShieldCheck } from "lucide-react";
export type StatusTone = "green" | "blue" | "amber" | "red" | "slate";
export function StatusChip({
  label,
  tone = "slate"
}: {
  label: string;
  tone?: StatusTone;
}) {
  const styles: Record<StatusTone, string> = {
    green: "border-emerald-200 bg-emerald-50 text-emerald-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    red: "border-red-200 bg-red-50 text-red-800",
    slate: "border-slate-200 bg-slate-50 text-slate-700"
  };
  const Icon = tone === "green" ? CheckCircle2 : tone === "blue" ? Info : tone === "amber" ? Clock3 : tone === "red" ? AlertCircle : FileWarning;
  return <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${styles[tone]}`} data-id="element-91" data-mp="23v10py1y8wt0"><Icon className="h-3.5 w-3.5" data-id="element-92" data-mp="20rynmvyk49k" />{label}</span>;
}
export function SectionTitle({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-93" data-mp="3r3yckuvil8ud">
    <div data-id="element-94" data-mp="3d45l2xc5yolc">
      {eyebrow && <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[#006a4e]" data-id="element-95" data-mp="3d4ymdiscslw3">{eyebrow}</p>}
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-id="element-96" data-mp="3i30fkwjhu1la">{title}</h1>
      {description && <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600" data-id="element-97" data-mp="9ngpg6e4czag">{description}</p>}
    </div>
    {action}
  </div>;
}
export function KpiCard({
  label,
  value,
  detail,
  tone = "green"
}: {
  label: string;
  value: string;
  detail: string;
  tone?: StatusTone;
}) {
  const colors: Record<StatusTone, string> = {
    green: "border-t-[#006a4e]",
    blue: "border-t-blue-600",
    amber: "border-t-amber-500",
    red: "border-t-red-600",
    slate: "border-t-slate-500"
  };
  return <article className={`rounded-xl border border-slate-200 border-t-4 bg-white p-4 shadow-sm ${colors[tone]}`} data-id="element-98" data-mp="xdszpnwu7847">
    <p className="text-sm font-medium text-slate-600" data-id="element-99" data-mp="1765kft0k4y56">{label}</p>
    <p className="mt-2 text-2xl font-bold text-slate-900" data-id="element-100" data-mp="12a6rds4aaryf">{value}</p>
    <p className="mt-1 text-xs text-slate-500" data-id="element-101" data-mp="x9she4j4h7xr">{detail}</p>
  </article>;
}
export function InfoPanel({
  children,
  tone = "blue"
}: {
  children: React.ReactNode;
  tone?: "blue" | "amber" | "green";
}) {
  const styles = tone === "blue" ? "border-blue-200 bg-blue-50 text-blue-900" : tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900";
  return <div className={`flex gap-3 rounded-xl border p-4 text-sm leading-6 ${styles}`} data-id="element-102" data-mp="eirjljtqnifs"><Info className="mt-0.5 h-5 w-5 shrink-0" data-id="element-103" data-mp="9kq1b144zn5d" /> <div data-id="element-104" data-mp="9lsp3065x9h2">{children}</div></div>;
}
export function MapPlaceholder({
  compact = false
}: {
  compact?: boolean;
}) {
  return <div className={`relative overflow-hidden rounded-xl border border-dashed border-[#006a4e]/40 bg-[#eaf5ef] ${compact ? "h-40" : "h-64"}`} aria-label="মানচিত্রের নমুনা অবস্থান" data-id="element-105" data-mp="4klgx003y89v">
    <div className="absolute left-[12%] top-[15%] h-20 w-32 rotate-12 rounded-[35%] border-2 border-[#006a4e]/30" data-id="element-106" data-mp="2c6dbiapcgpvl" />
    <div className="absolute bottom-[10%] right-[14%] h-24 w-40 -rotate-6 rounded-[45%] border-2 border-[#006a4e]/30" data-id="element-107" data-mp="2g6x6p8qcxjf3" />
    <div className="absolute left-[43%] top-[40%] h-4 w-4 rounded-full bg-[#f42a41] ring-4 ring-[#f42a41]/20" data-id="element-108" data-mp="2l60doypwdzpp" />
    <div className="absolute inset-x-0 bottom-0 border-t border-[#006a4e]/15 bg-white/80 px-3 py-2 text-xs font-medium text-[#006a4e]" data-id="element-109" data-mp="1sj5qhu1p4m8j"><MapPin className="mr-1 inline h-3.5 w-3.5" data-id="element-110" data-mp="1xjabnxdju553" /> মানচিত্র সংযোগের ডেমো প্লেসহোল্ডার — প্রকৃত GPS/সীমানা তথ্য সংযুক্ত নয়</div>
  </div>;
}
export function PrivacyNote() {
  return <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-111" data-mp="277s9q6ug1y51"><ShieldCheck className="h-4 w-4 text-[#006a4e]" data-id="element-112" data-mp="27a4yb2upj6r2" /> সংবেদনশীল তথ্য ভূমিকা অনুযায়ী আড়াল করা হয়েছে</div>;
}